import React from 'react'
import { styled } from '@mui/material/styles';
import { Link, Stack, Accordion, AccordionDetails, Button } from '@mui/material';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import CircleIcon from '@mui/icons-material/Circle';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import styles from "./BillingListAccordion.module.scss";
import { formatNumber, dateToString } from "../../libs/helpers";
import GeneralDialog from '../../components/core/GeneralDialog';
import { InvoiceListResponseData, InvoiceMasterData } from "../../apis/models/InvoiceMasterRequest";
import InvoiceDownloadDialog from '../dialogs/InvoiceDownloadDialog';
import { callDownloadInvoiceApi, getInvoiceMaster } from '../../apis/invmaster-api';
import { InvStatus } from '../../libs/constants';


const BillingListAccordion = (props: BillingListAccordionProps) => {
  const { item, index, expanded, handleAccordionChange, onChangeInvStatus } = props;
  const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem'}} />}
        {...props}
    />
    ))(({ theme }) => ({
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1),
    },
  }));

  const [openInvoiceConfirmationDialog, setOpenInvoiceConfirmationDialog] = React.useState<boolean>(false);
  const [downloadData, setDownloadData] = React.useState<{open: boolean; invoice?: InvoiceMasterData}>({open: false, invoice: undefined});

  const getIndex = (panelId:string | false) => {
    if(typeof(panelId) === "string") {
        return Number(panelId.substring( 5, 5 ));
    }else return -1;
  }

  const handleClickDownloadInvoice = async () => {
    const response = await getInvoiceMaster(item.invoiceMasterId)
    const invoiceData = response.data.data as InvoiceMasterData
    setDownloadData({
      open: true,
      invoice: invoiceData
    })
  }

  const downloadInvoice = async (pdf: boolean, excel: boolean) => {
    setDownloadData({
      open: false,
      invoice: undefined
    })

    const response = await callDownloadInvoiceApi(item.invoiceMasterId, pdf, excel)
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `請求書_ブランド_${item.brandNo}_${item.year}${String(item.month).padStart(2, '0')}.zip`);
    document.body.appendChild(link);
    link.click();

    item.status = InvStatus.EXPORT
    onChangeInvStatus(getIndex(expanded), InvStatus.EXPORT)
  }

  return (
    <Accordion key={index} className={styles.billingListAccordion} expanded={expanded === `panel${index}`} onChange={handleAccordionChange(`panel${index}`)}> 
      <AccordionSummary
      aria-controls={`panel${index}-content`}
      id={`panel${index}-header`}
      sx={{ padding: "24px", justifyContent: "space-between"}}
      >
          <div className={styles.billingListItem}>
              <div className={styles.billingData}>
                  <Stack className={styles.billingAmount} direction="row" alignItems="flex-end" justifyContent="space-between" spacing={1}>
                      <Stack justifyContent="flex-end" style={{ width: "calc(100%/3)" }}>
                          <p style={{ margin: "0 0 0 8px", color: "#5B5B5B"}}>請求金額</p>
                          <p style={{ margin:"0 0 0 8px", fontSize: "24px", whiteSpace: "nowrap", textAlign: "right" }}>{formatNumber(item.totalPrice)}<span style={{ fontSize:"16px", marginLeft: "0" }}>円(税込)</span> </p>
                      </Stack>
                      <div className={styles.price}>{formatNumber(Math.round(item.totalPrice / 1.1))}<span style={{ fontSize:"16px", marginLeft: "0" }}>円(税抜)</span></div>
                      <div className={styles.price}>{formatNumber(Math.round(item.totalPrice * 0.1))}<span style={{ fontSize:"16px", marginLeft: "0" }}>円(消費税額)</span></div>
                  </Stack>
                  <Stack direction="row" alignItems="flex-end" sx={{marginLeft: "24px"}}>
                      { 
                          item.status === "not yet confirmed" ? <><CircleIcon sx={{color: "#E88B00"}} fontSize="small"/> <div> 未確定 </div></>
                          : item.status === "confirmed" ? <><CircleIcon sx={{color: "#3A830F"}} fontSize="small"/><div> 確定済 </div></>
                          : <><CircleIcon sx={{color: "#1E53A3"}} fontSize="small"/><div> 請求書発行済 </div></>
                      }
                  </Stack>
                  <div>
                      {
                          `${dateToString(item.from)} ~ `                                            
                      }
                      {
                          item.to && dateToString(item.to)                                            
                      }
                  </div>
              </div>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ right: "24px"}}>                                    
                  {
                      item.status === "not yet confirmed" ? <Button variant="outlined" color="success" sx={{ whiteSpace: "nowrap" }} onClick={() => setOpenInvoiceConfirmationDialog(true)}>請求確定</Button>
                      : <Button variant="outlined" color="success" sx={{ whiteSpace: "nowrap" }} onClick={(e) => {e.stopPropagation(); handleClickDownloadInvoice()}} >請求書ダウンロード</Button>
                  }
                  <GeneralDialog
                      open={openInvoiceConfirmationDialog} 
                      onCancel={() => setOpenInvoiceConfirmationDialog(false)} 
                      cancelText="編集に戻る" 
                      onSave={() => {onChangeInvStatus(getIndex(expanded), InvStatus.CONFIRMED); setOpenInvoiceConfirmationDialog(false)}} 
                      saveText="請求確定" 
                      title="請求確定" 
                      onClose={() => setOpenInvoiceConfirmationDialog(false)}
                      >
                      <div>
                          下記の請求を確定しますか？<br/><br/>
                          {item.issuedTo}<br/>
                      </div>
                      <div>{`請求期間：${dateToString(item.from)} ~ ${dateToString(item.to)}`}</div>
                  </GeneralDialog>
                  {(downloadData.open && downloadData.invoice) &&
                    <InvoiceDownloadDialog
                      targetInvoice={downloadData.invoice}
                      open={true}
                      onClose={() => setDownloadData({open: false, invoice: undefined})}
                      onDownload={(invoice, pdf, excel) => downloadInvoice(pdf, excel)}
                    />
                  }
                  <Link href={`/invoice/${item.invoiceMasterId}`} sx={{ whiteSpace: "nowrap" }} onClick={(e) => {e.stopPropagation()}}>詳細を見る&gt;</Link>
              </Stack>
          </div>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "0 24px 40px 68px"}}>
				<div className={styles.Table}>
						<div className={styles.Table__head}>
							<div></div>
							<div>数量</div>
							<div>単価</div>
							<div>合計金額</div>
							<div>摘要</div>
						</div>
						{ 
						item.details !== undefined &&
							item.details.map((_item, _index) => (
								<div className={styles.Table__row} key={_index}>
										<div className={styles.Table__row_name}>{_item.name}</div>
										<div className={styles.Table__row_amount}>{_item.quantity === null ? "-" : formatNumber(_item.quantity)}</div>
										<div className={styles.Table__row_unitprice}>{_item.unitPrice === null ? "-" : formatNumber(_item.unitPrice)}</div>
										<div className={styles.Table__row_totalprice}>{formatNumber(_item.totalPrice)}</div>                                                                 
										<div className={styles.Table__row_description}>{_item.description}</div>
								</div>
							))
						}
				</div>
      </AccordionDetails>
    </Accordion>
  )
}

export default BillingListAccordion;

export type BillingListAccordionProps = {
  item: InvoiceListResponseData,
  index: number,
  expanded: string | false,
  handleAccordionChange: (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => void,
  onChangeInvStatus: (inputIndex:number, status: InvStatus) => void,
};